import { useEffect } from "react";
import { useNavigateCarrying } from "@/lib/carryParams";
import { NAV_CHORD, NAV_LABEL_KEY, NAV_TREE } from "@/lib/navTree";
import { tr } from "@/lib/i18n";

/**
 * Vim-style two-stroke navigation. After pressing `g`, the next key picks the
 * destination — so muscle memory of `g o` (overview), `g a` (alarms), etc.
 * works for SCADA operators coming from terminal-style HMIs.
 *
 * Conventions:
 *   - Bindings are NEVER swallowed inside <input>, <textarea>, <select>, or
 *     [contenteditable] elements (so typing into the alarm filter or the
 *     comment box still works normally).
 *   - `?` opens the shortcut sheet (handled by App via openShortcuts).
 *   - `cmd/ctrl+K` opens the command palette (handled by App).
 *   - `Esc` closes any open modal/drawer (we dispatch a custom event).
 */
// Built from the single chord source (lib/navTree NAV_CHORD), inverted from
// path→key to key→path so `g <key>` resolves to a route. One place to rebind.
const NAV_BINDINGS: Record<string, string> = Object.fromEntries(
  Object.entries(NAV_CHORD).map(([path, key]) => [key, path]),
);

export function useKeyboardShortcuts(opts: {
  onPaletteOpen: () => void;
  onShortcutsOpen: () => void;
  onSearchFocus?: () => void;
}) {
  // Carrying navigate: a `g h` hop keeps the same time window a tab click
  // would keep — identical-looking navigations must not behave differently.
  const navigate = useNavigateCarrying();
  useEffect(() => {
    let pendingG = 0;

    function isEditable(target: EventTarget | null) {
      if (!(target instanceof HTMLElement)) return false;
      if (target.isContentEditable) return true;
      const t = target.tagName;
      return t === "INPUT" || t === "TEXTAREA" || t === "SELECT";
    }

    function handler(e: KeyboardEvent) {
      // Cmd+K / Ctrl+K — palette is always reachable, even from inputs.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        opts.onPaletteOpen();
        return;
      }
      if (e.key === "Escape") {
        window.dispatchEvent(new CustomEvent("ems:escape"));
        return;
      }

      // The remaining shortcuts must NOT trigger from inside inputs.
      if (isEditable(e.target)) return;

      if (e.key === "?") {
        e.preventDefault();
        opts.onShortcutsOpen();
        return;
      }
      if (e.key === "/") {
        e.preventDefault();
        opts.onSearchFocus?.();
        return;
      }
      if (e.key.toLowerCase() === "g") {
        pendingG = Date.now();
        return;
      }
      // 1-second window after pressing `g` to pick a destination.
      if (Date.now() - pendingG < 1000) {
        const dest = NAV_BINDINGS[e.key.toLowerCase()];
        if (dest) {
          e.preventDefault();
          pendingG = 0;
          navigate(dest);
          return;
        }
      }
    }
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, opts]);
}

// Nav rows are DERIVED from the single nav tree + chord source so they can
// never drift from the tab strip / palette: every tab carrying a g-chord
// becomes one "g <key>" row, labelled by its translated nav key. SHORTCUT_HELP
// is a module const (not a hook), so labels use the imperative tr() — current
// enough since ShortcutHelpDialog re-imports it each time it opens. The
// non-nav rows (⌘K, /, ?, Esc) stay literal.
const NAV_HELP_ROWS: { keys: string; label: string }[] = NAV_TREE.flatMap((m) =>
  m.tabs
    .filter((tab) => NAV_CHORD[tab.path])
    .map((tab) => ({
      keys: `g ${NAV_CHORD[tab.path]}`,
      label: `Go to ${tr(NAV_LABEL_KEY[tab.label] ?? tab.label)}`,
    })),
);

export const SHORTCUT_HELP: { keys: string; label: string }[] = [
  { keys: "⌘K", label: "Open command palette" },
  ...NAV_HELP_ROWS,
  { keys: "/", label: "Focus filter / search" },
  { keys: "?", label: "Show this help" },
  { keys: "Esc", label: "Close modal / dismiss" },
];
